import { uploadsTargetTransport } from "./targetInitiation";

/**
 * The three durable columns a chunked-object upload row needs to resume on the SAME lane after a
 * reload: which completion body the driver must send, and the two facts a tab with no File left
 * rebuilds its manifest and upload order from.
 */
export interface ChunkedObjectLaneColumns {
	transport?: "multipart" | "chunked-object";
	chunkSizeBytes?: number;
	priorityChunkIndexesJson?: string;
}

/** The optional block a chunked-object `/uploads` target carries alongside its envelopes. */
export interface ChunkedObjectTargetBlock {
	chunkSizeBytes: number;
	priorityChunkIndexes: number[];
}

/**
 * ONE projection rule for every writer of those columns in BOTH web apps — contact-async's
 * `targetPersistenceFields` and `reassignFileUploadSessionTarget`, contact-admin's
 * `targetPersistenceFields` and `createSession` — because the three columns only make sense
 * together: a row with `transport: "chunked-object"` and no chunk size cannot rebuild its manifest
 * and silently demotes itself to multipart on the next re-mint. It lives in the shared package
 * rather than in one app's helpers so the two apps cannot drift on the rule.
 *
 * - No `/uploads` target, or one minted multipart: all three cleared. `undefined` is what a multipart
 *   row has always looked like, and a stale `"chunked-object"` here would send the attempt fence for
 *   an attempt the server expects a parts body for.
 * - Chunked-object WITH its block: written from the block.
 * - Chunked-object WITHOUT a block (a re-normalized pass-through that carries no lane data): the
 *   row's existing values are retained rather than blanked, since the slicing did not change.
 */
export function chunkedObjectLaneColumns(
	target: ({ transport?: string } & { chunkedObject?: ChunkedObjectTargetBlock }) | undefined,
	existing?: Pick<ChunkedObjectLaneColumns, "chunkSizeBytes" | "priorityChunkIndexesJson">
): ChunkedObjectLaneColumns {
	if (!target || uploadsTargetTransport(target) !== "chunked-object") {
		return { transport: undefined, chunkSizeBytes: undefined, priorityChunkIndexesJson: undefined };
	}
	const block = target.chunkedObject;
	if (!block) {
		return {
			transport: "chunked-object",
			chunkSizeBytes: existing?.chunkSizeBytes,
			priorityChunkIndexesJson: existing?.priorityChunkIndexesJson
		};
	}
	return {
		transport: "chunked-object",
		chunkSizeBytes: block.chunkSizeBytes,
		priorityChunkIndexesJson: JSON.stringify(block.priorityChunkIndexes)
	};
}

/**
 * Read back a persisted header-first order. Browser storage is untyped, so a malformed value is
 * treated as "no preference" (an EMPTY order, never a partial one): the server does not validate
 * order, but a partial permutation would leave chunks the loop never walks.
 */
export function parsePersistedPriorityChunkIndexes(json: string | undefined, sessionId: string): number[] {
	if (!json) return [];
	let parsed: unknown;
	try {
		parsed = JSON.parse(json);
	} catch {
		console.warn("[Upload] Persisted chunked-object priority order is not valid JSON; resuming with no order preference", {
			sessionId
		});
		return [];
	}
	if (!Array.isArray(parsed) || !parsed.every((index) => Number.isSafeInteger(index) && (index as number) >= 0)) {
		console.warn("[Upload] Persisted chunked-object priority order is malformed; resuming with no order preference", { sessionId });
		return [];
	}
	return parsed as number[];
}
